var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { DocumentRetrievalFailureReason, DocumentRetrievalLocationStatus, DocumentVectorizationStatus, } from "healthex-common/document.retrieval.dtos";
import { isErr, isOk } from "healthex-common/result";
import { computed, observable, reaction } from "mobx";
import { PatientDataVisualizationPdfStatus } from "healthex-common/patient.data.visualization.dtos";
import { dtcOrganizationId } from "healthex-common/organization.dtos";
import { asEnum } from "healthex-common/utils";
export class DocumentRetrievalManager {
    get orgId() {
        var _a;
        return (_a = this.authManager.patientConsentOrgId) !== null && _a !== void 0 ? _a : dtcOrganizationId;
    }
    get patientId() {
        var _a;
        return (_a = this.authManager.id) !== null && _a !== void 0 ? _a : "";
    }
    constructor(documentRetrievalService, authManager) {
        this.documentRetrievalService = documentRetrievalService;
        this.authManager = authManager;
        this.pollingTimerId = null;
        this.pendingRetrievalKeys = new Set();
        this.retrievals = [];
        this.retrievalErrors = [];
        reaction(() => this.retrievals.map((r) => r), async (retrievals) => {
            const anyRetrievalInProgress = retrievals.some((retrieval) => this.isRetrievalInProgress(retrieval));
            if (anyRetrievalInProgress) {
                await this.startPollingTimerIfNeeded();
            }
            else {
                this.clearPollingTimerIfNeeded();
            }
        }, { fireImmediately: true });
    }
    async getExistingInProgressDocumentRetrieval(studyId) {
        const batch = await this.getExistingRetrieval(studyId, undefined, false);
        if (!batch)
            return null;
        await this.updateBatch(batch);
        return batch;
    }
    async initiateDocumentRetrieval(studyId, isDataUpdate) {
        var _a, _b, _c, _d, _e;
        const retrievalKey = `${this.patientId}-${studyId !== null && studyId !== void 0 ? studyId : "null"}`;
        if (this.pendingRetrievalKeys.has(retrievalKey)) {
            return null;
        }
        this.pendingRetrievalKeys.add(retrievalKey);
        let batch = (_a = this.retrievals.find((retrieval) => retrieval.patientId === this.patientId &&
            retrieval.studyId === studyId &&
            !retrieval.documentSelectionComplete &&
            retrieval.isDataUpdate === isDataUpdate)) !== null && _a !== void 0 ? _a : null;
        if (batch) {
            batch = await this.refreshBatch(batch.batchId);
            if (batch === null || batch === void 0 ? void 0 : batch.documentSelectionComplete) {
                batch = null;
            }
        }
        if (!batch) {
            let batchResult;
            if (isDataUpdate) {
                batchResult = await this.documentRetrievalService.retrieveUpdatedPatientData(this.orgId, this.patientId, {
                    studyId,
                });
            }
            else {
                batchResult = await this.documentRetrievalService.retrievePatientData(this.orgId, this.patientId, {
                    studyId,
                });
            }
            if (isErr(batchResult)) {
                this.pendingRetrievalKeys.delete(retrievalKey);
                const errorBatch = await this.getExistingInProgressDocumentRetrieval(studyId);
                batch = errorBatch;
            }
            else {
                batch = batchResult;
            }
            if (!batch)
                return null;
            const medAlliesGetDocumentsError = batch.medAlliesGetDocumentsError
                ? (_c = asEnum(DocumentRetrievalFailureReason)((_b = batch.medAlliesGetDocumentsError.replace(/^ForbiddenException: /, "")) !== null && _b !== void 0 ? _b : "")) !== null && _c !== void 0 ? _c : DocumentRetrievalFailureReason.GENERIC_ERROR
                : undefined;
            const medAlliesGetEndpointsError = batch.medAlliesGetEndpointsError
                ? (_e = asEnum(DocumentRetrievalFailureReason)((_d = batch.medAlliesGetEndpointsError.replace(/^ForbiddenException: /, "")) !== null && _d !== void 0 ? _d : "")) !== null && _e !== void 0 ? _e : DocumentRetrievalFailureReason.GENERIC_ERROR
                : undefined;
            if (!!medAlliesGetDocumentsError || !!medAlliesGetEndpointsError) {
                this.setMedAlliesError({
                    batchId: batch.batchId,
                    medAlliesGetDocumentsError,
                    medAlliesGetEndpointsError,
                });
            }
        }
        await this.updateBatch(batch);
        this.pendingRetrievalKeys.delete(retrievalKey);
        return batch.batchId;
    }
    async retryDocumentRetrieval(batchId) {
        const batch = await this.refreshBatch(batchId);
        if (!batch)
            return;
        this.clearMedAlliesError(batchId);
        if (!batch.medAlliesGetDocumentsComplete || !batch.medAlliesGetEndpointsComplete) {
            void this.getDocumentsAndEndpoints(batch.batchId);
        }
    }
    async skipDocumentRetrieval(batchId) {
        const batch = await this.refreshBatch(batchId);
        if (!batch)
            return;
        this.clearMedAlliesError(batchId);
        const updatedBatch = await this.documentRetrievalService.skipMedAlliesRetrieval(this.patientId, batchId);
        if (isOk(updatedBatch)) {
            await this.updateBatch(updatedBatch);
        }
    }
    async getDownloadPdfLink(batchId) {
        return await this.documentRetrievalService.getTaskBatchDownloadPdfLink(this.patientId, batchId);
    }
    async startPollingTimerIfNeeded() {
        this.clearPollingTimerIfNeeded();
        this.pollingTimerId = window.setInterval(async () => {
            await this.refreshInProgressBatches();
        }, 1000);
    }
    clearPollingTimerIfNeeded() {
        if (this.pollingTimerId !== null) {
            window.clearInterval(this.pollingTimerId);
        }
    }
    isRetrievalInProgress(retrieval) {
        if (!retrieval.medAlliesGetDocumentsComplete || !retrieval.medAlliesGetEndpointsComplete) {
            return true;
        }
        if (!retrieval.documentSelectionComplete) {
            if (retrieval.locations.some((location) => location.status === DocumentRetrievalLocationStatus.IN_PROGRESS)) {
                return true;
            }
            return false;
        }
        if (retrieval.isAIFlow &&
            (retrieval.documentVectorizationStatus === DocumentVectorizationStatus.NOT_STARTED ||
                retrieval.documentVectorizationStatus === DocumentVectorizationStatus.IN_PROGRESS)) {
            return true;
        }
        const retrievalHasDocuments = retrieval.documents.length > 0;
        const pdfIsCompleted = retrieval.pdfStatus === PatientDataVisualizationPdfStatus.COMPLETED ||
            retrieval.pdfStatus === PatientDataVisualizationPdfStatus.ERROR;
        if (!retrieval.finalized || (retrievalHasDocuments && !pdfIsCompleted)) {
            return true;
        }
        return false;
    }
    async refreshBatch(batchId) {
        if (!batchId) {
            console.warn(`Batch id is not valid`);
            return null;
        }
        const batch = await this.documentRetrievalService.getTaskBatch(this.orgId, this.patientId, batchId);
        if (!batch) {
            console.warn(`Batch ${batchId} not found`);
            return null;
        }
        await this.updateBatch(batch);
        return batch;
    }
    async refreshInProgressBatches() {
        await Promise.all(this.retrievals.map(async (retrieval) => {
            if (!this.isRetrievalInProgress(retrieval))
                return;
            await this.refreshBatch(retrieval.batchId);
        }));
    }
    async getExistingRetrieval(studyId, batchId, documentSelectionComplete) {
        return await this.documentRetrievalService.getExistingRetrieval(this.orgId, this.patientId, {
            batchId,
            studyId,
            documentSelectionComplete,
        });
    }
    async updateBatch(batch) {
        if (!batch)
            return;
        const index = this.retrievals.findIndex((retrieval) => retrieval.batchId === batch.batchId);
        if (index !== -1) {
            this.retrievals[index] = batch;
        }
        else {
            this.retrievals.push(batch);
        }
    }
    async getDocumentsAndEndpoints(batchId) {
        const batch = await this.documentRetrievalService.getDocumentsAndEndpoints(this.patientId, batchId);
        if (isErr(batch)) {
            this.setMedAlliesError({
                batchId,
                medAlliesGetDocumentsError: batch.error,
            });
        }
        else {
            await this.updateBatch(batch);
        }
    }
    setMedAlliesError(errors) {
        const existingErrorIndex = this.retrievalErrors.findIndex((error) => error.batchId === errors.batchId);
        if (existingErrorIndex !== -1) {
            this.retrievalErrors[existingErrorIndex] = {
                ...this.retrievalErrors[existingErrorIndex],
                ...errors,
            };
        }
        else {
            this.retrievalErrors.push(errors);
        }
    }
    clearMedAlliesError(batchId) {
        this.retrievalErrors = this.retrievalErrors.filter((error) => error.batchId !== batchId);
    }
    async getFacilitatedFHIRAuthUrl(batchId, fhirEndpoint, redirectUrl, authType) {
        return await this.documentRetrievalService.getFacilitatedFHIRAuthUrl(this.patientId, batchId, fhirEndpoint, redirectUrl, authType);
    }
    async getFacilitatedFHIRRecords(batchId, locationId, fhirEndpoint, authType, redirectUri, code) {
        const batch = await this.documentRetrievalService.getFacilitatedFHIRRecords(this.patientId, batchId, locationId, fhirEndpoint, authType, redirectUri, code);
        await this.updateBatch(batch);
    }
    async getFastenDocuments(batchId, connectionId, portalId, brandId) {
        const batch = await this.documentRetrievalService.trackFastenRetrieval(this.patientId, batchId, connectionId, portalId, brandId);
        await this.updateBatch(batch);
    }
    async retrieveDocument(batchId, documentId) {
        const batch = await this.documentRetrievalService.retrieveFacilitatedFHIRDocument(this.orgId, this.patientId, batchId, documentId);
        await this.updateBatch(batch);
    }
    async skipLocation(batchId, locationId) {
        const batch = await this.documentRetrievalService.updateTaskBatch(this.patientId, batchId, {
            skipLocationId: locationId,
        });
        await this.updateBatch(batch);
    }
    async doneSelectingDocuments(batchId) {
        const batch = await this.documentRetrievalService.updateTaskBatch(this.patientId, batchId, {
            documentSelectionComplete: true,
        });
        await this.updateBatch(batch);
    }
    async downloadInitiated(batchId) {
        const batch = await this.documentRetrievalService.updateTaskBatch(this.patientId, batchId, {
            patientHasDownloaded: true,
        });
        await this.updateBatch(batch);
    }
    async getDownloadDocumentsLink(batchId) {
        return await this.documentRetrievalService.getTaskBatchDownloadLink(this.patientId, batchId);
    }
    async getMostRecentDownloadableOrProcessingTaskBatch() {
        return await this.documentRetrievalService.getMostRecentDownloadableOrProcessingTaskBatch(this.patientId);
    }
}
__decorate([
    observable,
    __metadata("design:type", Array)
], DocumentRetrievalManager.prototype, "retrievals", void 0);
__decorate([
    observable,
    __metadata("design:type", Array)
], DocumentRetrievalManager.prototype, "retrievalErrors", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalManager.prototype, "orgId", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalManager.prototype, "patientId", null);
